import { useState, useMemo, Fragment } from 'react';
import {
  CheckCircle,
  XCircle,
  AlertTriangle,
  Info,
  PlayCircle,
  FileText,
  Upload,
  Database,
  Send,
  Ban,
  Loader2,
  ChevronDown,
  Filter,
  Clock,
  User,
  Monitor,
  Server,
} from 'lucide-react';
import { clsx } from 'clsx';
import { useActivities, useActivitySummary } from '../../hooks/useActivityLog';
import type { Activity } from '../../api/activityLog';

// ── Icon/colour maps for activity types ──

const TYPE_ICONS: Record<string, typeof CheckCircle> = {
  'order.submitted': Send,
  'order.created': Info,
  'order.processing': PlayCircle,
  'order.completed': CheckCircle,
  'order.failed': XCircle,
  'order.noHit': AlertTriangle,
  'order.error': AlertTriangle,
  'order.canceled': Ban,
  'order.canceling': Ban,
  'order.resumed': PlayCircle,
  'vendorOrder.created': Server,
  'vendorOrder.completed': CheckCircle,
  'vendorOrder.failed': XCircle,
  'vendorOrder.canceled': Ban,
  'vendorOrder.canceling': Ban,
  'vendor.cancel.order.submitted': Send,
  'vendor.cancel.order.error': AlertTriangle,
  'vendor.cancel.order.accepted': CheckCircle,
  'document.uploaded': Upload,
  'document.uploadFailed': AlertTriangle,
  'data.uploaded': Database,
  'data.mapped': Database,
  'data.uploadFailed': AlertTriangle,
  'order.validation.error': AlertTriangle,
  'order.logic.cancelled': Ban,
  'order.logic.order.submitted': Send,
  'report.threshold.not.met': AlertTriangle,
};

const TYPE_COLORS: Record<string, { bg: string; text: string }> = {
  'order.completed': { bg: 'bg-green-500', text: 'text-green-600 dark:text-green-400' },
  'vendorOrder.completed': { bg: 'bg-green-500', text: 'text-green-600 dark:text-green-400' },
  'vendor.cancel.order.accepted': { bg: 'bg-green-500', text: 'text-green-600 dark:text-green-400' },
  'order.failed': { bg: 'bg-red-500', text: 'text-red-600 dark:text-red-400' },
  'order.noHit': { bg: 'bg-amber-500', text: 'text-amber-600 dark:text-amber-400' },
  'order.error': { bg: 'bg-red-500', text: 'text-red-600 dark:text-red-400' },
  'vendorOrder.failed': { bg: 'bg-red-500', text: 'text-red-600 dark:text-red-400' },
  'document.uploadFailed': { bg: 'bg-red-500', text: 'text-red-600 dark:text-red-400' },
  'data.uploadFailed': { bg: 'bg-red-500', text: 'text-red-600 dark:text-red-400' },
  'vendor.cancel.order.error': { bg: 'bg-red-500', text: 'text-red-600 dark:text-red-400' },
  'order.validation.error': { bg: 'bg-red-500', text: 'text-red-600 dark:text-red-400' },
  'report.threshold.not.met': { bg: 'bg-amber-500', text: 'text-amber-600 dark:text-amber-400' },
  'order.canceled': { bg: 'bg-gray-400', text: 'text-gray-600 dark:text-gray-400' },
  'order.canceling': { bg: 'bg-gray-400', text: 'text-gray-600 dark:text-gray-400' },
  'vendorOrder.canceled': { bg: 'bg-gray-400', text: 'text-gray-600 dark:text-gray-400' },
  'order.logic.cancelled': { bg: 'bg-gray-400', text: 'text-gray-600 dark:text-gray-400' },
  'order.submitted': { bg: 'bg-blue-500', text: 'text-blue-600 dark:text-blue-400' },
  'order.created': { bg: 'bg-blue-500', text: 'text-blue-600 dark:text-blue-400' },
  'order.processing': { bg: 'bg-amber-500', text: 'text-amber-600 dark:text-amber-400' },
  'order.resumed': { bg: 'bg-purple-500', text: 'text-purple-600 dark:text-purple-400' },
  'document.uploaded': { bg: 'bg-cyan-500', text: 'text-cyan-600 dark:text-cyan-400' },
  'data.uploaded': { bg: 'bg-cyan-500', text: 'text-cyan-600 dark:text-cyan-400' },
  'data.mapped': { bg: 'bg-cyan-500', text: 'text-cyan-600 dark:text-cyan-400' },
};

const SEVERITY_STYLES: Record<string, { badge: string; dot: string }> = {
  info: { badge: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300', dot: 'bg-blue-400' },
  warning: { badge: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300', dot: 'bg-amber-400' },
  critical: { badge: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300', dot: 'bg-red-400' },
};

const ACTOR_ICONS: Record<string, typeof User> = {
  user: User,
  system: Monitor,
  vendor: Server,
};

const DEFAULT_COLOR = { bg: 'bg-gray-400', text: 'text-gray-600 dark:text-gray-400' };

function getIcon(type: string) {
  return TYPE_ICONS[type] || Info;
}

function getColor(type: string) {
  return TYPE_COLORS[type] || DEFAULT_COLOR;
}

function formatActivityDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  });
}

// ── Severity filter options ──

const SEVERITY_OPTIONS = [
  { value: '', label: 'All severities' },
  { value: 'info', label: 'Info' },
  { value: 'warning', label: 'Warning' },
  { value: 'critical', label: 'Critical' },
];

// ── Unique activity type categories for filtering ──

const TYPE_CATEGORIES = [
  { value: '', label: 'All types' },
  { value: 'order.', label: 'Order lifecycle' },
  { value: 'vendorOrder.', label: 'Vendor orders' },
  { value: 'vendor.cancel.', label: 'Vendor cancel' },
  { value: 'document.', label: 'Documents' },
  { value: 'data.', label: 'Data mapping' },
  { value: 'report.', label: 'Reports' },
  { value: 'order.logic.', label: 'Order logic' },
];

// ── Activity entry ──

function ActivityEntry({ activity }: { activity: Activity }) {
  const [expanded, setExpanded] = useState(false);
  const Icon = getIcon(activity.type);
  const color = getColor(activity.type);
  const severity = SEVERITY_STYLES[activity.severity] || SEVERITY_STYLES.info;
  const ActorIcon = ACTOR_ICONS[activity.actor?.type] || User;

  return (
    <div className="relative flex gap-3 pb-6 last:pb-0 group">
      {/* Timeline line */}
      <div className="absolute left-[13px] top-[26px] bottom-0 w-[2px] bg-gray-200 dark:bg-gray-700/50 group-last:hidden" />

      {/* Node */}
      <div
        className={clsx(
          'relative z-10 flex items-center justify-center w-[26px] h-[26px] rounded-full shrink-0 ring-[3px] ring-white dark:ring-gray-900',
          color.bg,
        )}
      >
        <Icon className="h-3.5 w-3.5 text-white" />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0 pt-0.5">
        <div className="flex items-start gap-2 flex-wrap">
          <span className="text-[13px] font-semibold text-gray-900 dark:text-gray-100">
            {activity.title || activity.type}
          </span>
          <span className={clsx('inline-flex items-center gap-1 text-[10px] rounded-full px-2 py-0.5', severity.badge)}>
            <span className={clsx('w-1.5 h-1.5 rounded-full', severity.dot)} />
            {activity.severity}
          </span>
          <span className="text-[10px] text-gray-400 bg-gray-100 dark:bg-gray-800 rounded px-1.5 py-0.5 font-mono">
            {activity.type}
          </span>
        </div>

        {activity.description && (
          <p className="mt-1 text-xs text-gray-600 dark:text-gray-400 line-clamp-2">
            {activity.description}
          </p>
        )}

        <div className="flex items-center gap-3 mt-1.5 flex-wrap">
          <span className="text-[11px] text-gray-500">
            <Clock className="h-3 w-3 inline mr-1 -mt-0.5" />
            {formatActivityDate(activity.occurredAt)}
          </span>
          {activity.actor && (
            <span className="text-[11px] text-gray-500">
              <ActorIcon className="h-3 w-3 inline mr-1 -mt-0.5" />
              {activity.actor.displayName || activity.actor.id || activity.actor.type}
            </span>
          )}
          {activity.source?.system && (
            <span className="text-[10px] text-gray-400 bg-gray-50 dark:bg-gray-800/50 rounded px-1.5 py-0.5">
              {activity.source.system}
            </span>
          )}
          {activity.channel && (
            <span className="text-[10px] text-gray-400 bg-gray-50 dark:bg-gray-800/50 rounded px-1.5 py-0.5">
              {activity.channel}
            </span>
          )}
        </div>

        {/* Expandable attributes */}
        {activity.attributes && Object.keys(activity.attributes).length > 0 && (
          <button
            onClick={() => setExpanded(!expanded)}
            className="mt-1.5 text-[11px] text-blue-500 hover:text-blue-400 flex items-center gap-1"
          >
            <ChevronDown className={clsx('h-3 w-3 transition-transform', expanded && 'rotate-180')} />
            {expanded ? 'Hide' : 'Show'} details
          </button>
        )}
        {expanded && activity.attributes && (
          <pre className="mt-2 text-[11px] text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-950/50 rounded p-3 overflow-auto max-h-48 border border-gray-200 dark:border-gray-700/50 font-mono">
            {JSON.stringify(activity.attributes, null, 2)}
          </pre>
        )}
      </div>
    </div>
  );
}

// ── Summary badges ──

export function ActivitySummaryBadges({ orderUrn }: { orderUrn: string }) {
  const { data: summary, isLoading } = useActivitySummary(orderUrn);

  if (isLoading || !summary || summary.totalCount === 0) return null;

  const warnings = summary.countsBySeverity?.warning || 0;
  const criticals = summary.countsBySeverity?.critical || 0;

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span className="inline-flex items-center gap-1 text-[11px] rounded-full px-2.5 py-1 bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">
        <FileText className="h-3 w-3" />
        {summary.totalCount} activities
      </span>
      {warnings > 0 && (
        <span className="inline-flex items-center gap-1 text-[11px] rounded-full px-2.5 py-1 bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
          <AlertTriangle className="h-3 w-3" />
          {warnings} warning{warnings !== 1 ? 's' : ''}
        </span>
      )}
      {criticals > 0 && (
        <span className="inline-flex items-center gap-1 text-[11px] rounded-full px-2.5 py-1 bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300">
          <XCircle className="h-3 w-3" />
          {criticals} critical
        </span>
      )}
    </div>
  );
}

// ── Main component ──

interface OrderActivityLogProps {
  orderUrn: string;
}

export default function OrderActivityLog({ orderUrn }: OrderActivityLogProps) {
  const [severityFilter, setSeverityFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');

  const params = useMemo(
    () => ({
      ...(severityFilter ? { severity: severityFilter } : {}),
      ...(typeFilter ? { type: typeFilter } : {}),
      limit: 20,
    }),
    [severityFilter, typeFilter],
  );

  const {
    data,
    isLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useActivities(orderUrn, params);

  const { data: summary } = useActivitySummary(orderUrn);

  const activities = useMemo(
    () => data?.pages.flatMap((p) => p.items) || [],
    [data],
  );

  return (
    <div className="space-y-4">
      {/* Summary bar */}
      {summary && summary.totalCount > 0 && (
        <div className="flex items-center justify-between gap-4 p-3 rounded bg-gray-50 dark:bg-gray-800/30 border border-gray-200 dark:border-gray-700/50">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {summary.totalCount} total activities
            </span>
            {Object.entries(summary.countsBySeverity || {}).map(([sev, count]) => (
              <span
                key={sev}
                className={clsx(
                  'inline-flex items-center gap-1 text-[11px] rounded-full px-2 py-0.5',
                  SEVERITY_STYLES[sev]?.badge || SEVERITY_STYLES.info.badge,
                )}
              >
                <span className={clsx('w-1.5 h-1.5 rounded-full', SEVERITY_STYLES[sev]?.dot || SEVERITY_STYLES.info.dot)} />
                {count} {sev}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-1.5 text-xs text-gray-500">
          <Filter className="h-3.5 w-3.5" />
          Filters:
        </div>
        <select
          value={severityFilter}
          onChange={(e) => setSeverityFilter(e.target.value)}
          className="text-xs rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-2.5 py-1.5 text-gray-700 dark:text-gray-300"
        >
          {SEVERITY_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="text-xs rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-2.5 py-1.5 text-gray-700 dark:text-gray-300"
        >
          {TYPE_CATEGORIES.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>

      {/* Timeline */}
      {isLoading ? (
        <div className="flex items-center gap-2 py-8 justify-center text-sm text-gray-500 dark:text-gray-400">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading activities...
        </div>
      ) : activities.length === 0 ? (
        <div className="text-center py-8">
          <FileText className="h-8 w-8 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
          <p className="text-sm text-gray-500 dark:text-gray-400">No activity recorded for this order</p>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Activities will appear here as the order is processed</p>
        </div>
      ) : (
        <div className="pl-1">
          {activities.map((activity) => (
            <Fragment key={activity.id || `${activity.occurredAt}-${activity.type}`}>
              <ActivityEntry activity={activity} />
            </Fragment>
          ))}

          {/* Load more */}
          {hasNextPage && (
            <div className="pt-2 pl-[38px]">
              <button
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
                className="text-xs text-blue-500 hover:text-blue-400 flex items-center gap-1"
              >
                {isFetchingNextPage ? (
                  <>
                    <Loader2 className="h-3 w-3 animate-spin" /> Loading more...
                  </>
                ) : (
                  'Load more activities'
                )}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
